import { useStore } from '@hub/helpers/use-store';
import { Box, IconButton, Stack, Tooltip } from '@mui/material';
import { SystemIcon } from '@monorepo/nova-icons';
import { Typography, useBreakpoints } from '@monorepo/nova-ui';
import {
  Account,
  AccountBalance,
  ConsistencyRules,
  Program,
} from '@monorepo/types';
import {
  DEFAULT_VALUES,
  OBJECTIVE_ICONS,
  PROFITABLE_DAYS_CONSTANTS,
} from '../constants/objective-constants';
import { useConsistencyRule, useRuleQueryUI } from '../hooks';
import { ObjectiveData } from '../types';
import { BaseObjectiveProps, useObjectiveBase } from './base';
import {
  StyledCapTag,
  StyledMainTypography,
  StyledSecondaryTypography,
} from './consistency-rule-objective.styled';

interface ConsistencyRuleObjectiveProps extends BaseObjectiveProps {
  account?: Account;
  accountBalance?: AccountBalance;
  program: Program | undefined | null;
}

/**
 * Consistency Rule widget component
 * Shows consistency percentage for best trade profit limit rule
 */
export const ConsistencyRuleObjective = ({
  account,
  program,
}: ConsistencyRuleObjectiveProps): ObjectiveData => {
  const { t, theme } = useObjectiveBase();
  const { isMobile } = useBreakpoints();
  const {
    dataStore: { programStore },
  } = useStore();

  // Get the rule value from program configuration
  const bestTradeProfitLimitRule = program?._id
    ? programStore.getRule(program._id, ConsistencyRules.BestTradeProfitLimit)
    : null;

  const bestTradeProfitLimitValue = bestTradeProfitLimitRule?.value;

  // Use standardized hook for consistency rule query (SOLID: Single Responsibility)
  const {
    data: ruleResult,
    isLoading,
    error,
    refetch,
  } = useConsistencyRule(
    account,
    ConsistencyRules.BestTradeProfitLimit,
    !!bestTradeProfitLimitValue,
  );

  // Use standardized UI hook for loading/error states (SOLID: Separation of Concerns)
  const { getUIState } = useRuleQueryUI({
    isLoading,
    error,
    hasData: !!ruleResult,
    onRetry: () => refetch(),
    errorMessage: t('objectives.consistencyRule.error'),
    retryLabel: t('objectives.consistencyRule.retry'),
    noDataMessage: t('objectives.consistencyRule.noData'),
  });

  // If no rule configured, don't render
  if (!bestTradeProfitLimitValue) {
    const primaryLabel = (
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: theme.spacing(1),
        }}
      >
        <Typography
          component="span"
          variant="body2"
          sx={{ color: theme.palette.text.second }}
        >
          {t('objectives.consistencyRule.label')}
        </Typography>
      </Box>
    );
    return {
      icon: (
        <SystemIcon
          iconName="Umbrella"
          size={DEFAULT_VALUES.OBJECTIVE_ICON_SIZE}
        />
      ),
      primaryText: primaryLabel,
      secondaryText: null,
    };
  }

  // Handle loading/error/no-data states using standardized UI (SOLID: Open/Closed)
  const uiState = getUIState(theme);
  if (uiState) {
    const primaryLabel = (
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: theme.spacing(1),
        }}
      >
        <Typography
          component="span"
          variant="body2"
          sx={{ color: theme.palette.text.second }}
        >
          {t('objectives.consistencyRule.label')}
        </Typography>
      </Box>
    );
    return {
      icon: (
        <SystemIcon
          iconName="Umbrella"
          size={DEFAULT_VALUES.OBJECTIVE_ICON_SIZE}
        />
      ),
      primaryText: primaryLabel,
      secondaryText: uiState,
    };
  }

  // If no data after UI state check, don't render (fallback)
  if (!ruleResult) {
    const primaryLabel = (
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: theme.spacing(1),
        }}
      >
        <Typography
          component="span"
          variant="body2"
          sx={{ color: theme.palette.text.second }}
        >
          {t('objectives.consistencyRule.label')}
        </Typography>
      </Box>
    );
    return {
      icon: (
        <SystemIcon
          iconName="Umbrella"
          size={DEFAULT_VALUES.OBJECTIVE_ICON_SIZE}
        />
      ),
      primaryText: primaryLabel,
      secondaryText: null,
    };
  }

  const currentPercentage = ruleResult.current || 0;
  const isExceeded =
    !ruleResult.isValid || currentPercentage > bestTradeProfitLimitValue;

  const tooltipContent = !isMobile ? (
    <Stack gap={1}>
      <Typography
        variant="body2"
        sx={{
          color: theme.palette.text.opposite,
          lineHeight: theme.typography.body2.lineHeight,
        }}
      >
        {isExceeded
          ? t('objectives.consistencyRule.tooltipExceeded', {
              total: bestTradeProfitLimitValue,
            })
          : t('objectives.consistencyRule.tooltip', {
              total: bestTradeProfitLimitValue,
            })}
      </Typography>
    </Stack>
  ) : undefined;

  const primaryLabel = (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: theme.spacing(1),
      }}
    >
      <Typography
        component="span"
        variant="body2"
        sx={{ color: theme.palette.text.second }}
      >
        {t('objectives.consistencyRule.label')}
      </Typography>
      {!isMobile && tooltipContent && (
        <Tooltip title={tooltipContent}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              sx={{
                color: theme.palette.primary.main,
              }}
            >
              <SystemIcon
                iconName={OBJECTIVE_ICONS.INFO}
                size={PROFITABLE_DAYS_CONSTANTS.INFO_ICON_SIZE}
                sx={{
                  opacity: 0.6,
                }}
              />
            </IconButton>
          </Box>
        </Tooltip>
      )}
    </Box>
  );

  return {
    icon: (
      <SystemIcon
        iconName="Umbrella"
        size={DEFAULT_VALUES.OBJECTIVE_ICON_SIZE}
      />
    ),
    primaryText: primaryLabel,
    secondaryText: (
      <Stack gap={3} flex={1}>
        <Stack
          alignItems={{ sm: 'flex-start' }}
          justifyContent="space-between"
          flex={1}
        >
          <Stack gap={0}>
            <StyledMainTypography variant="subtitle1" isExceeded={isExceeded}>
              {t('objectives.consistencyRule.current', {
                current: Math.round(currentPercentage),
              })}
            </StyledMainTypography>
            <StyledSecondaryTypography variant="body2">
              {t('objectives.consistencyRule.of', {
                total: bestTradeProfitLimitValue,
              })}
            </StyledSecondaryTypography>
          </Stack>
          <StyledCapTag
            label={t('objectives.consistencyRule.capLimit', {
              total: bestTradeProfitLimitValue,
            })}
            size="small"
            isExceeded={isExceeded}
          />
        </Stack>
      </Stack>
    ),
  };
};
